import axios from 'axios';

const TOKEN_KEY = 'koolsign_auth_token';
const TOKEN_EXPIRY_KEY = 'koolsign_token_expiry';
const AUTH_RETURN_HASH_KEY = 'koolsign_auth_return_hash';
const TOKEN_LIFETIME_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

type TokenResponse =
    | string
    | {
          token?: string;
          accessToken?: string;
          access_token?: string;
          value?: string;
      };

export function isAdvertVipMgrStandaloneRoute(hash = window.location.hash): boolean {
    const routePath = hash.replace(/^#\/?/, '').split('?')[0].replace(/\/+$/, '');
    return routePath === 'advertVipMgrStandalone';
}

function rememberAuthReturnHash(): void {
    if (isAdvertVipMgrStandaloneRoute()) {
        sessionStorage.setItem(AUTH_RETURN_HASH_KEY, window.location.hash);
    }
}

function takeAuthReturnHash(): string | null {
    const returnHash = sessionStorage.getItem(AUTH_RETURN_HASH_KEY);
    sessionStorage.removeItem(AUTH_RETURN_HASH_KEY);
    return returnHash && isAdvertVipMgrStandaloneRoute(returnHash) ? returnHash : null;
}

// 获取 API baseURL
const getAPIBaseURL = () => {
    if (process.env.API_HOST) {
        return process.env.API_HOST;
    }
    // @ts-ignore - CONFIG is defined by webpack DefinePlugin
    if (typeof CONFIG !== 'undefined' && CONFIG.IS_DEV_OR_TESTING) {
        return 'https://sit.api.kuguanggao.com';
    }
    return 'https://api.kuguanggao.com';
};

/**
 * 从 URL 查询参数中提取 code
 */
export function extractCodeFromURL(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code');
}

/**
 * 从 localStorage 获取 token
 */
export function getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || token === 'undefined' || token === 'null' || token === '[object Object]') {
        if (token === '[object Object]') {
            clearToken();
        }
        return null;
    }
    return token;
}

function normalizeToken(payload: TokenResponse): string | null {
    if (typeof payload === 'string') {
        return payload;
    }

    if (!payload || typeof payload !== 'object') {
        return null;
    }

    return payload.token || payload.accessToken || payload.access_token || payload.value || null;
}

/**
 * 保存 token 到 localStorage
 */
export function setToken(tokenPayload: TokenResponse): void {
    const token = normalizeToken(tokenPayload);

    if (!token || token === 'undefined' || token === 'null') {
        clearToken();
        return;
    }
    localStorage.setItem(TOKEN_KEY, token);
    // 保存过期时间戳
    const expiryTime = Date.now() + TOKEN_LIFETIME_MS;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
}

/**
 * 清除 token
 */
export function clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

/**
 * 检查 token 是否即将过期（剩余时间少于 5 分钟）
 */
export function isTokenExpiringSoon(): boolean {
    const expiryTimeStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTimeStr) {
        return true;
    }

    const expiryTime = parseInt(expiryTimeStr, 10);
    const timeRemaining = expiryTime - Date.now();
    const FIVE_MINUTES_MS = 5 * 60 * 1000;

    return timeRemaining < FIVE_MINUTES_MS;
}

/**
 * 检查 token 是否已过期
 * 增加 1 分钟的缓冲区，防止请求发出后在服务器端过期
 */
export function isTokenExpired(): boolean {
    const expiryTimeStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTimeStr) {
        return true;
    }

    const expiryTime = parseInt(expiryTimeStr, 10);
    const BUFFER_MS = 60 * 1000; // 1 minute buffer
    return Date.now() >= expiryTime - BUFFER_MS;
}

/**
 * 使用 code 获取 token
 */
export async function fetchTokenWithCode(code: string): Promise<string> {
    const baseURL = getAPIBaseURL();
    const response = await axios.get(`${baseURL}/api/kkj/site/ad/auth/token`, {
        params: { code },
    });
    console.log(response);
    const token = normalizeToken(response.data.d);
    if (!token) {
        throw new Error('Invalid token response');
    }
    setToken(token);
    return token;
}

/**
 * 刷新 token
 */
export async function refreshToken(): Promise<string> {
    try {
        const currentToken = getToken();
        if (!currentToken) {
            throw new Error('No token available to refresh');
        }

        const baseURL = getAPIBaseURL();
        const response = await axios.get(`${baseURL}/api/kkj/site/ad/auth/token/refresh`, {
            headers: {
                token: currentToken,
            },
        });

        const newToken = normalizeToken(response.data.d);
        if (!newToken) {
            throw new Error('Invalid refresh token response');
        }
        setToken(newToken);
        return newToken;
    } catch (error) {
        clearToken();
        redirectToLogin();
        throw error;
    }
}

/**
 * 跳转到 OAuth 登录页面
 */
export async function redirectToLogin(): Promise<void> {
    const { removeAdvertHostWidgets } = await import('./advertHostWidgets');
    await removeAdvertHostWidgets();

    rememberAuthReturnHash();

    const clientId = 'I0QfeYFSwmwDYBjq';
    // 优先从 URL 参数中获取 state，如果没有则根据域名判断
    const urlParams = new URLSearchParams(window.location.search);
    let state = urlParams.get('state');

    if (state !== 'beta' && state !== 'prod') {
        state = window.location.hostname === 'beta.kuguanggao.com' ? 'beta' : 'prod';
    }

    // 重定向地址统一使用 manifest 中定义的地址
    const redirectUri = encodeURIComponent('https://prod.kuguanggao.com/pub/cspace/koolsign/main');
    const scope = 'get_user_info,get_design,get_budget_list,opt_kds_design,get_render_pic';

    const oauthUrl = `https://oauth.kujiale.com/oauth2/show?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=${state}&scope=${scope}`;

    window.location.href = oauthUrl;
}

/**
 * 退出登录：清除 token 并跳转到登录页面
 */
export async function logout(): Promise<void> {
    clearToken();
    await redirectToLogin();
}

/**
 * 初始化认证：尝试从 URL 获取 code 并换取 token
 * 如果 URL 中没有 code，检查本地是否有有效 token
 */
export async function initializeAuth(): Promise<boolean> {
    // 1. 尝试从 URL 获取 code
    const code = extractCodeFromURL();
    if (code) {
        try {
            await fetchTokenWithCode(code);
            // 清除 URL 中的 code 参数
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            const returnHash = takeAuthReturnHash();
            if (returnHash) {
                url.hash = returnHash;
            }
            window.history.replaceState({}, '', url.toString());
            return true;
        } catch (error) {
            console.error('Failed to fetch token with code:', error);
            clearToken();
            redirectToLogin();
            return false;
        }
    }

    // 2. 检查本地是否有有效 token
    const existingToken = getToken();
    if (existingToken) {
        // 如果 token 已过期，尝试刷新
        if (isTokenExpired()) {
            try {
                await refreshToken();
                return true;
            } catch (error) {
                console.error('Failed to refresh expired token:', error);
                return false;
            }
        }

        // 如果 token 即将过期，后台刷新
        if (isTokenExpiringSoon()) {
            refreshToken().catch(error => {
                console.error('Background token refresh failed:', error);
            });
        }

        return true;
    }

    // 3. 没有 code 也没有 token，需要进行 OAuth 登录
    redirectToLogin();
    return false;
}

/**
 * 启动自动刷新定时器（每 1.5 小时检查一次）
 */
export function startTokenRefreshTimer(): number {
    const CHECK_INTERVAL_MS = 90 * 60 * 1000; // 1.5 hours

    return setInterval(async () => {
        const token = getToken();
        if (!token) {
            return;
        }

        if (isTokenExpiringSoon()) {
            try {
                await refreshToken();
                console.log('Token refreshed successfully');
            } catch (error) {
                console.error('Failed to refresh token:', error);
            }
        }
    }, CHECK_INTERVAL_MS) as unknown as number;
}
